import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// ── 모바일 감지 훅 ──────────────────────────────────────────
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

// ── 색상 토큰 (메인화면과 동일) ──────────────────────────────
const BLUE    = '#3a8fd4';
const BLUE_BG = '#e6f1fb';

// ── 아이콘 ───────────────────────────────────────────────────
const Ico = {
  Target: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>,
  Bolt:   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
  Cal:    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
};

function CardHeader({ icon, title }) {
  return (
    <div style={S.cardHeader}>
      <div style={S.cardIcon}>{icon}</div>
      <h2 style={S.cardTitle}>{title}</h2>
    </div>
  );
}

function AIAssistantPage() {
  const navigate  = useNavigate();
  const isMobile  = useIsMobile();

  const [loading,       setLoading]       = useState(true);
  const [userName,      setUserName]      = useState('사용자');
  const [aiSuggestions, setAiSuggestions] = useState(null);

  const [showCancelModal,          setShowCancelModal]          = useState(false);
  const [showNoTimeModal,          setShowNoTimeModal]          = useState(false);
  const [showReminderSuccessModal, setShowReminderSuccessModal] = useState(false);
  const [reminderMessage,          setReminderMessage]          = useState('');

  const apiUrl = process.env.REACT_APP_API_URL || 'https://campus-life-production.up.railway.app';

  useEffect(() => {
    const loggedInUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
    if (loggedInUser?.name) setUserName(loggedInUser.name);
    const uid = loggedInUser?.userId || '2';

    fetch(`${apiUrl}/api/schedules/${uid}`)
      .then(res => res.json())
      .then(data => {
        const today    = new Date();
        const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;

        const priority = data
          .filter(s => !s.isDone)
          .slice(0, 2)
          .map((s, i) => ({
            id:     s.id,
            title:  s.title,
            reason: s.type === 'EXAM' ? '시험 범위 복습 필요' : '마감 임박 및 진척도 낮음',
            level:  i === 0 ? '매우 높음' : '높음',
          }));

        const plans = data.slice(0, 2).map((s, i) => ({
          id:   `p${i}`,
          date: todayStr,
          time: i === 0 ? '14:00 - 16:00' : '19:00 - 21:00',
          task: `${s.title} 작업`,
          tip:  s.type === 'EXAM' ? '핵심 내용 요약 권장' : '단계별 진행 권장',
        }));

        const activeSuggestions = data.slice(0, 2).map((s, i) => ({
          id:          `s${i}`,
          type:        i === 0 ? '추천' : '경고',
          text:        i === 0 ? `"${s.title}" 작업을 오늘 완료해볼까요?` : `"${s.title}" 마감 전에 미리 준비하세요.`,
          date:        todayStr,
          defaultTime: i === 0 ? '10:00' : '18:00',
        }));

        setAiSuggestions({
          priority: priority.length > 0 ? priority : [{ id: 0, title: '등록된 과제 없음', reason: '플래너에서 일정을 추가해주세요', level: '없음' }],
          plans:    plans.length > 0 ? plans : [{ id: 'p0', date: todayStr, time: '14:00 - 16:00', task: '오늘의 학습 시작', tip: '플래너에서 일정을 추가해주세요' }],
          activeSuggestions: activeSuggestions.length > 0 ? activeSuggestions : [{ id: 's0', type: '추천', text: '플래너에서 일정을 먼저 추가해주세요!', date: todayStr, defaultTime: '10:00' }],
        });
        setLoading(false);
      })
      .catch(() => {
        const today    = new Date();
        const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;
        setAiSuggestions({
          priority:         [{ id: 0, title: '백엔드 연결 실패', reason: '서버를 확인해주세요', level: '없음' }],
          plans:            [{ id: 'p0', date: todayStr, time: '14:00 - 16:00', task: '서버 연결 후 다시 시도', tip: '' }],
          activeSuggestions:[{ id: 's0', type: '경고', text: '백엔드 서버에 연결할 수 없습니다.', date: todayStr, defaultTime: '10:00' }],
        });
        setLoading(false);
      });
  }, [apiUrl]);

  const confirmCancelAll = () => {
    localStorage.removeItem('aiSavedSession');
    localStorage.removeItem('ai_pending_events');
    setShowCancelModal(false);
  };

  if (loading) {
    return (
      <div style={S.root}>
        <div style={S.loadingWrap}>
          <div style={S.spinner} />
          <p style={S.loadingText}>📊 일정 분석 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...S.root, overflowX: 'hidden' }}>

      <div style={{
        ...S.outerWrap,
        // 모바일: 패딩 줄이기
        padding: isMobile ? '16px 14px 60px' : '32px 32px 60px',
      }}>

        {/* ── 상단 버튼 + 타이틀 (모바일: 세로 배치) ── */}
        <div style={{
          ...S.topRow,
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'flex-start' : 'center',
          gap: isMobile ? '8px' : '10px',
          marginBottom: isMobile ? '14px' : '20px',
        }}>
          <button style={S.backBtn} onClick={() => navigate(-1)}>← 플래너로 돌아가기</button>
          <h1 style={{ ...S.pageTitle, fontSize: isMobile ? '16px' : '20px' }}>✨ AI 학습 비서</h1>
        </div>

        {/* ── 메인 그리드 (모바일: 1열) ── */}
        <div style={{ width: '100%', maxWidth: '1100px' }}>
          <div style={{
            ...S.grid,
            // 모바일: 1열
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: isMobile ? '12px' : '20px',
            rowGap: isMobile ? '12px' : '28px',
          }}>

            {/* AI 우선순위 분석 */}
            <div style={S.card}>
              <CardHeader icon={Ico.Target} title="AI 우선순위 분석" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {aiSuggestions.priority.map(p => (
                  <div key={p.id} style={S.infoBox}>
                    <div style={S.infoRow}>
                      <span style={S.infoTitle}>{p.title}</span>
                      <span style={S.levelBadge}>{p.level}</span>
                    </div>
                    <p style={S.infoReason}>{p.reason}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* 능동적 일정 제안 */}
            <div style={S.card}>
              <CardHeader icon={Ico.Bolt} title="능동적 일정 제안" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {aiSuggestions.activeSuggestions.map(s => (
                  <div key={s.id} style={S.suggestBox}>
                    <span style={s.type === '경고' ? S.badgeWarn : S.badgeReco}>{s.type}</span>
                    <p style={S.suggestText}>{s.text}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* 타임블로킹 (모바일: 1열 전체, 데스크탑: 2열 span) */}
            <div style={{
              ...S.card,
              gridColumn: isMobile ? '1' : '1 / span 2',
            }}>
              <CardHeader icon={Ico.Cal} title={`${userName}님을 위한 오늘의 추천 Time-Blocking`} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {aiSuggestions.plans.map((plan, idx) => (
                  <div key={plan.id}>
                    <div style={{
                      ...S.timelineItem,
                      // 모바일: 세로 배치
                      flexDirection: isMobile ? 'column' : 'row',
                      gap: isMobile ? '6px' : '16px',
                    }}>
                      <span style={{
                        ...S.timeLabel,
                        width: isMobile ? 'auto' : '110px',
                      }}>{plan.time}</span>
                      <div style={S.planBox}>
                        <p style={S.planTask}>{plan.task}</p>
                        {plan.tip && <p style={S.planTip}>{plan.tip}</p>}
                      </div>
                    </div>
                    {idx < aiSuggestions.plans.length - 1 && <div style={S.divider} />}
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ── 알림 설정 성공 모달 ── */}
      {showReminderSuccessModal && (
        <div style={S.modalOverlay}>
          <div style={S.modal}>
            <p style={S.modalMsg}>{reminderMessage}</p>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <button style={S.modalBtn} onClick={() => setShowReminderSuccessModal(false)}>확인</button>
            </div>
          </div>
        </div>
      )}

      {/* ── 시간 미선택 모달 ── */}
      {showNoTimeModal && (
        <div style={S.modalOverlay}>
          <div style={S.modal}>
            <p style={S.modalMsg}>날짜를 선택해주세요.</p>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <button style={S.modalBtn} onClick={() => setShowNoTimeModal(false)}>확인</button>
            </div>
          </div>
        </div>
      )}

      {/* ── 취소 확인 모달 ── */}
      {showCancelModal && (
        <div style={S.modalOverlay}>
          <div style={S.modal}>
            <p style={S.modalMsg}>정말 모든 설정을 취소하시겠습니까?</p>
            <div style={S.modalBtnRow}>
              <button style={S.secondaryBtn} onClick={() => setShowCancelModal(false)}>취소</button>
              <button style={S.primaryBtn}   onClick={confirmCancelAll}>확인</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

// ── 스타일 (원본 그대로) ─────────────────────────────────────
const S = {
  root: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    minHeight: '100vh', backgroundColor: '#f8fafc',
    fontFamily: "'Pretendard', 'Apple SD Gothic Neo', sans-serif",
  },
  outerWrap:  { display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', padding: '32px 32px 60px', boxSizing: 'border-box' },
  topRow:     { display: 'flex', alignItems: 'center', gap: '10px', width: '100%', maxWidth: '1400px', marginBottom: '20px' },
  backBtn:    { padding: '9px 14px', backgroundColor: '#f1f5f9', color: '#374151', border: '1px solid #e2e8f0', borderRadius: '10px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap' },
  pageTitle:  { fontSize: '20px', fontWeight: '800', color: '#1e293b', margin: 0, whiteSpace: 'nowrap' },
  grid:       { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', rowGap: '28px', width: '100%', maxWidth: '1100px' },
  card: {
    backgroundColor: '#fff', borderRadius: '14px', padding: '20px 22px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.04)',
    border: '1px solid #f1f5f9', boxSizing: 'border-box', minWidth: 0,
  },
  cardHeader: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' },
  cardIcon:   { width: '28px', height: '28px', borderRadius: '8px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: BLUE_BG, color: BLUE },
  cardTitle:  { margin: 0, fontSize: '14px', fontWeight: '600', color: '#1e293b' },
  infoBox:    { padding: '12px 14px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px' },
  infoRow:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' },
  infoTitle:  { fontSize: '13px', fontWeight: '600', color: '#1e293b' },
  levelBadge: { padding: '2px 7px', backgroundColor: BLUE_BG, color: BLUE, borderRadius: '5px', fontSize: '10px', fontWeight: '600' },
  infoReason: { margin: 0, fontSize: '12px', color: '#64748b', lineHeight: '1.5' },
  suggestBox: { display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '12px 14px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px' },
  badgeWarn:  { padding: '3px 8px', backgroundColor: '#fee2e2', color: '#b91c1c', borderRadius: '5px', fontSize: '11px', fontWeight: '700', whiteSpace: 'nowrap', flexShrink: 0 },
  badgeReco:  { padding: '3px 8px', backgroundColor: BLUE_BG,   color: BLUE,      borderRadius: '5px', fontSize: '11px', fontWeight: '700', whiteSpace: 'nowrap', flexShrink: 0 },
  suggestText:{ margin: 0, fontSize: '13px', color: '#374151', fontWeight: '500', lineHeight: '1.6' },
  timelineItem: { display: 'flex', alignItems: 'stretch', gap: '16px' },
  timeLabel:    { width: '110px', fontWeight: '600', color: BLUE, fontSize: '12px', flexShrink: 0, paddingTop: '2px' },
  planBox:      { flex: 1, backgroundColor: '#f8fafc', padding: '12px 14px', borderRadius: '10px', border: '1px solid #e2e8f0' },
  planTask:     { margin: 0, fontSize: '13px', fontWeight: '600', color: '#1e293b' },
  planTip:      { margin: '3px 0 0', fontSize: '12px', color: '#94a3b8' },
  divider:      { height: '1px', backgroundColor: '#f1f5f9', margin: '6px 0' },
  loadingWrap:  { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: '14px', padding: '100px 0' },
  spinner:      { width: '28px', height: '28px', border: '3px solid #bfdbfe', borderTop: `3px solid ${BLUE}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  loadingText:  { fontSize: '14px', color: '#64748b', margin: 0 },
  modalOverlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.35)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 },
  modal:        { backgroundColor: '#fff', padding: '32px 40px', borderRadius: '14px', boxShadow: '0 20px 40px rgba(0,0,0,0.12)', textAlign: 'center', minWidth: '280px' },
  modalMsg:     { fontSize: '16px', fontWeight: '600', color: '#1e293b', margin: '0 0 24px', lineHeight: '1.5' },
  modalBtnRow:  { display: 'flex', gap: '10px', justifyContent: 'center' },
  modalBtn:     { padding: '9px 24px', backgroundColor: BLUE, color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  primaryBtn:   { flex: 1, padding: '11px', backgroundColor: BLUE, color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  secondaryBtn: { flex: 1, padding: '11px', backgroundColor: '#f1f5f9', color: '#374151', border: '1px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
};

if (typeof document !== 'undefined' && !document.getElementById('spin-style-ai')) {
  const style       = document.createElement('style');
  style.id          = 'spin-style-ai';
  style.textContent = '@keyframes spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(style);
}

export default AIAssistantPage;